import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Autocomplete,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  VpnKey as KeyIcon,
  GroupAdd as GroupAddIcon,
  Info as InfoIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  FilterList as FilterListIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { usersAPI } from '../services/api';
import AddEntitlementsDialog from '../components/AddEntitlementsDialog';
import UserDetailsDialog from '../components/UserDetailsDialog';
import PaginationControls from '../components/PaginationControls';
import DataSource from '../components/DataSource';
import { formatCreated } from '../utils/formatCreated';
import ColumnPicker from '../components/ColumnPicker';
import { useColumnPrefs } from '../hooks/useColumnPrefs';
import {
  fetchUsers,
  fetchOUs,
  clearUserCache,
  addUserOptimistic,
  updateUserOptimistic,
  removeUserOptimistic,
} from '../store/dataSlice';

function Users() {
  const dispatch = useDispatch();
  const ous = useSelector((state) => state.data.ous.data);

  // Pagination state
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');

  // Column definitions. `sort` names the key handleSortChange uses, so a
  // column stays sortable wherever the user drags it to.
  const USER_COLUMNS = React.useMemo(() => [
    { key: 'username', label: 'Username', sort: 'username', render: (u) => u.username },
    // displayName first. The list comes from the agent's list_users, which does
    // not request givenName/sn, so given_name+surname are empty for EVERY user
    // and this column rendered '-' for all of them. displayName is returned,
    // populated, and is what the directory itself shows as the person's name.
    { key: 'name', label: 'Name', sort: 'name',
      render: (u) => (u.displayName
        || `${u.given_name || ''} ${u.surname || ''}`.trim()
        || '-') },
    { key: 'mail', label: 'Email', sort: 'mail', render: (u) => u.mail || '-' },
    { key: 'ou', label: 'OU', sort: 'ou', render: (u) => u.ou || '-' },
    { key: 'status', label: 'Status', sort: 'enabled', render: (u) => (
        <Chip label={u.enabled ? 'Enabled' : 'Disabled'}
              color={u.enabled ? 'success' : 'default'} size="small" />
      ) },
    { key: 'created', label: 'Created', sort: 'whenCreated',
      sx: { whiteSpace: 'nowrap' }, render: (u) => formatCreated(u.whenCreated) },
    { key: 'title', label: 'Title', render: (u) => u.title || '-' , defaultHidden: true },
    { key: 'department', label: 'Department', render: (u) => u.department || '-' , defaultHidden: true },
    { key: 'mobile', label: 'Mobile', render: (u) => u.mobile || '-' , defaultHidden: true },
    { key: 'telephoneNumber', label: 'Telephone', render: (u) => u.telephoneNumber || '-' , defaultHidden: true },
    { key: 'employeeType', label: 'Employee type', render: (u) => u.employeeType || '-' , defaultHidden: true },
    { key: 'description', label: 'Description', render: (u) => u.description || '-' , defaultHidden: true },
  ], []);
  const { ordered, visibleColumns, toggle, move, reset } =
    useColumnPrefs('cols.users', USER_COLUMNS);
  // Which DC actually served this listing.
  const [dataSource, setDataSource] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const searchInputRef = useRef(null);

  const [error, setError] = useState('');
  // This page had no success state at all, so a working reset and a failing one both
  // just closed the dialog — which is why the reported symptom was that it never says
  // successful. See the password branch in handleSubmit.
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [entitlementsDialogOpen, setEntitlementsDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentUser, setCurrentUser] = useState(null);
  // Exactly what the edit dialog loaded from the directory. Save sends only
  // the fields that differ from it, so a field the operator never touched is
  // never transmitted -- and therefore can never be cleared by accident.
  const [editBaseline, setEditBaseline] = useState(null);
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    first_name: '',
    surname: '',
    email: '',
    manager: '',
    middle_initial: '',
    title: '',
    mobile: '',
    phone: '',
    role: '',
    department: '',
    logon_workstations: '',
    ou: '',
    company: '',
    description: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  });

  // Search, sort, and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('username');
  const [sortOrder, setSortOrder] = useState('asc');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'enabled', 'disabled'

  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);

  const loadUsers = async (currentPage = page, currentPageSize = pageSize, refresh = false, isSearchUpdate = false) => {
    try {
      // Only show full loading spinner on initial load, not on search updates
      if (!isSearchUpdate) {
        setLoading(true);
      }
      setError('');
      const params = {
        page: currentPage,
        page_size: currentPageSize,
      };
      // Dates are filtered SERVER-side, unlike the search box below. The
      // search filters only the rows already on this page, which is fine for
      // finding a name you can see; a date window has to consider every user
      // in the domain or "created this week" silently means "created this week
      // AND on page 1".
      if (createdAfter) params.created_after = createdAfter;
      if (createdBefore) params.created_before = createdBefore;
      if (refresh) {
        params.refresh = 'true';
      }
      const response = await usersAPI.list(params);
      let usersData = response.data.results || [];

      // Apply search filter (client-side)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        usersData = usersData.filter(user =>
          (user.username && user.username.toLowerCase().includes(query)) ||
          (user.given_name && user.given_name.toLowerCase().includes(query)) ||
          (user.surname && user.surname.toLowerCase().includes(query)) ||
          (user.mail && user.mail.toLowerCase().includes(query)) ||
          (user.ou && user.ou.toLowerCase().includes(query))
        );
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        const filterEnabled = statusFilter === 'enabled';
        usersData = usersData.filter(user => user.enabled === filterEnabled);
      }

      // Apply sorting (client-side)
      usersData.sort((a, b) => {
        let aVal, bVal;
        if (sortBy === 'name') {
          aVal = `${a.given_name || ''} ${a.surname || ''}`.toLowerCase();
          bVal = `${b.given_name || ''} ${b.surname || ''}`.toLowerCase();
        } else if (sortBy === 'whenCreated') {
          // Compare as time, not as text. These are ISO-8601 so lexical order
          // happens to agree — until one row is missing the field, where ''
          // would sort as the oldest date rather than as "unknown". Missing
          // values sort last in both directions.
          const at = a.whenCreated ? Date.parse(a.whenCreated) : null;
          const bt = b.whenCreated ? Date.parse(b.whenCreated) : null;
          if (at === null && bt === null) return 0;
          if (at === null) return 1;
          if (bt === null) return -1;
          return sortOrder === 'asc' ? at - bt : bt - at;
        } else {
          aVal = (a[sortBy] || '').toString().toLowerCase();
          bVal = (b[sortBy] || '').toString().toLowerCase();
        }

        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      setUsers(usersData);
      setTotalCount(usersData.length);
      setDataSource(response.data.source || null);
      setTotalPages(Math.ceil(usersData.length / currentPageSize));
      setPage(currentPage);
      setPageSize(currentPageSize);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      setLoadingManagers(true);
      const response = await usersAPI.listManagers();
      setManagers(response.data.users || []);
    } catch (err) {
      console.error('Failed to load managers:', err);
    } finally {
      setLoadingManagers(false);
    }
  };

  useEffect(() => {
    // Load OUs
    dispatch(fetchOUs());

    // Load users with pagination
    loadUsers(1, pageSize);

    // Listen for sudo session established event
    const handleSudoSession = () => {
      // Force refresh when sudo session established
      loadUsers(page, pageSize, true);
      dispatch(fetchOUs(true));
    };

    window.addEventListener('sudo-session-established', handleSudoSession);

    return () => {
      window.removeEventListener('sudo-session-established', handleSudoSession);
    };
  }, []);

  // Reload when search, sort, or filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadUsers(1, pageSize);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, sortBy, sortOrder, statusFilter, createdAfter, createdBefore]);

  const handleRefresh = () => {
    // Force refresh bypassing cache
    loadUsers(page, pageSize, true);
    dispatch(fetchOUs(true));
  };

  const handlePageChange = (event, newPage) => {
    loadUsers(newPage, pageSize);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    loadUsers(1, newPageSize); // Reset to page 1 when changing page size
  };

  const handleOpenDetails = (username) => {
    setSelectedUsername(username);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedUsername(null);
  };

  const handleOpenDialog = async (mode, user = null) => {
    setDialogMode(mode);
    setCurrentUser(user);

    if (mode === 'create') {
      // Load managers for dropdown
      loadManagers();
      // Every key the form writes, or a value left over from the last edit is
      // sent as part of the new account: the create payload is built from
      // formData, not from the fields on screen.
      setFormData({
        username: '',
        password: '',
        first_name: '',
        surname: '',
        email: '',
        manager: '',
        middle_initial: '',
        title: '',
        mobile: '',
        phone: '',
        role: '',
        department: '',
        logon_workstations: '',
        ou: '',
        company: '',
        description: '',
        street_address: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
      });
      setOpenDialog(true);
    } else if (mode === 'edit' && user) {
      // Load managers for dropdown
      loadManagers();

      // Fetch full user details
      try {
        setLoading(true);
        // refresh: read the directory, not the sync cache -- see api.js.
        const response = await usersAPI.get(user.username, { refresh: true });
        const userData = response.data;

        const loaded = {
          username: userData.username || user.username,
          first_name: userData.givenName || userData.given_name || '',
          surname: userData.sn || userData.surname || '',
          email: userData.mail || userData.email || '',
          manager: userData.manager || '',
          middle_initial: userData.initials || userData.middle_initial || '',
          title: userData.title || '',
          mobile: userData.mobile || '',
          phone: userData.telephoneNumber || userData.phone || '',
          role: userData.employeeType || userData.role || '',
          department: userData.department || '',
          logon_workstations: userData.userWorkstations || userData.logon_workstations || '',
          ou: userData.ou || '',
          company: userData.company || '',
          description: userData.description || '',
          street_address: userData.streetAddress || userData.postalAddress || '',
          city: userData.l || '',
          state: userData.st || '',
          postal_code: userData.postalCode || '',
          country: userData.co || userData.c || '',
        };
        setFormData(loaded);
        setEditBaseline(loaded);
        setOpenDialog(true);
      } catch (err) {
        setError('Failed to load user details: ' + (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    } else if (mode === 'password' && user) {
      setFormData({ username: user.username, password: '' });
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentUser(null);
    setEditBaseline(null);
    setManagers([]);
    setFormData({
      username: '',
      password: '',
      first_name: '',
      surname: '',
      email: '',
      manager: '',
      middle_initial: '',
      title: '',
      mobile: '',
      phone: '',
      role: '',
      department: '',
      logon_workstations: '',
      ou: '',
      company: '',
      description: '',
      street_address: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  const handleSubmit = async () => {
    if (dialogMode === 'create') {
      if (!formData.username || !formData.password || !formData.first_name ||
          !formData.surname || !formData.email || !formData.manager) {
        setError('Please fill in all required fields (username, password, first name, surname, email, manager)');
        return;
      }
      if (!validatePassword(formData.password)) {
        setError('Password must be at least 8 characters long');
        return;
      }
      if (!validateEmail(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }
    } else if (dialogMode === 'edit') {
      if (formData.email && !validateEmail(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }
    } else if (dialogMode === 'password') {
      if (!validatePassword(formData.password)) {
        setError('Password must be at least 8 characters long');
        return;
      }
    }

    try {
      if (dialogMode === 'create') {
        await usersAPI.create(formData);
        // Optimistic update
        dispatch(addUserOptimistic({
          username: formData.username,
          given_name: formData.first_name,
          surname: formData.surname,
          mail: formData.email,
          ou: formData.ou,
          enabled: true,
        }));
      } else if (dialogMode === 'edit') {
        // ONLY the fields the operator actually changed.
        //
        // This used to send all 18 every time, which made the request only as
        // correct as the pre-fill: a field loaded blank was submitted blank, and
        // a blank means "clear this attribute". With the dialog reading a stale
        // cache that returned empty givenName/sn/title/mobile, saving a rename
        // would have deleted the very attributes it displayed as empty.
        // Sending a diff means an untouched field is never transmitted, so it
        // cannot be cleared whatever the pre-fill said.
        const EDITABLE = [
          'first_name', 'surname', 'email', 'manager', 'middle_initial', 'title',
          'mobile', 'phone', 'role', 'department', 'logon_workstations',
          'company', 'description', 'street_address', 'city', 'state',
          'postal_code', 'country',
        ];
        const base = editBaseline || {};
        const updateData = {};
        EDITABLE.forEach((k) => {
          if ((formData[k] ?? '') !== (base[k] ?? '')) updateData[k] = formData[k];
        });
        if (Object.keys(updateData).length === 0) {
          setSuccess('No changes to save');
          handleCloseDialog();
          return;
        }
        await usersAPI.update(currentUser.username, updateData);
        // Optimistic update. displayName is included because the Name column
        // renders it first -- without it the row kept showing the OLD name
        // until the background refresh landed, which reads as "the rename did
        // nothing". The backend derives displayName the same way.
        const optimisticName = `${formData.first_name || ''} ${formData.surname || ''}`.trim();
        dispatch(updateUserOptimistic({
          username: currentUser.username,
          given_name: formData.first_name,
          surname: formData.surname,
          mail: formData.email,
          ...(optimisticName ? { displayName: optimisticName } : {}),
        }));
      } else if (dialogMode === 'password') {
        await usersAPI.resetPassword(currentUser.username, formData.password);
        setSuccess(`Password reset for ${currentUser.username}`);
      }
      handleCloseDialog();
      // Refresh in background to ensure data is accurate
      loadUsers(page, pageSize, true);
    } catch (err) {
      // DRF field validation returns {"new_password": ["This field is required."]} with no
      // `error` key, so reading only `.error` collapsed every validation failure into a bare
      // "Operation failed" and hid which field the API was objecting to.
      const data = err.response?.data;
      let msg = data?.error || data?.detail;
      if (!msg && data && typeof data === 'object') {
        msg = Object.entries(data)
          .map(([f, e]) => `${f}: ${Array.isArray(e) ? e.join(' ') : e}`)
          .join('; ');
      }
      setError(msg || 'Operation failed');
      // Refresh on error to ensure data is accurate
      loadUsers(page, pageSize, true);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user ${username}?`)) {
      return;
    }
    try {
      // Optimistic update
      dispatch(removeUserOptimistic(username));
      await usersAPI.delete(username);
      // Refresh in background
      dispatch(fetchUsers(true));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
      // Revert on error
      dispatch(fetchUsers(true));
    }
  };

  const handleDisable = async (username) => {
    if (!window.confirm(`Are you sure you want to disable user ${username}?`)) {
      return;
    }
    try {
      await usersAPI.disable(username);
      // Refresh to show updated status
      loadUsers(page, pageSize, true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable user');
    }
  };

  const handleEnable = async (username) => {
    try {
      await usersAPI.enable(username);
      // Refresh to show updated status
      loadUsers(page, pageSize, true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to enable user');
    }
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3,
                 flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4">Users</Typography>
          <DataSource source={dataSource} sx={{ mt: 0.5 }} />
        </Box>
        <Box>
          <ColumnPicker columns={ordered} onToggle={toggle} onMove={move} onReset={reset} />

          <IconButton onClick={handleRefresh} sx={{ mr: 1 }} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<GroupAddIcon />}
            onClick={() => setEntitlementsDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            + Entitlements
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Create User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search and Filter Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            placeholder="Search users by username, name, email, or OU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="enabled">Enabled Only</MenuItem>
              <MenuItem value="disabled">Disabled Only</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {/* Creation-date window. Either bound works alone, and both are
            inclusive, so "created on 3 March" is created_after = created_before
            = that day rather than an empty result. */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Created after" type="date" size="small"
            value={createdAfter} onChange={(e) => setCreatedAfter(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }}
          />
          <TextField
            label="Created before" type="date" size="small"
            value={createdBefore} onChange={(e) => setCreatedBefore(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }}
          />
          {(createdAfter || createdBefore) && (
            <Button size="small" onClick={() => { setCreatedAfter(''); setCreatedBefore(''); }}>
              Clear dates
            </Button>
          )}
        </Box>
        {(searchQuery || statusFilter !== 'all' || createdAfter || createdBefore) && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Found {totalCount} user{totalCount !== 1 ? 's' : ''}
              {searchQuery && ` matching "${searchQuery}"`}
              {statusFilter !== 'all' && ` (${statusFilter})`}
              {createdAfter && ` created on/after ${createdAfter}`}
              {createdBefore && ` created on/before ${createdBefore}`}
            </Typography>
          </Box>
        )}
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {visibleColumns.map((c) => (
                <TableCell key={c.key}>
                  {c.sort ? (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => handleSortChange(c.sort)}
                    >
                      {c.label}
                      {sortBy === c.sort && (
                        sortOrder === 'asc'
                          ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                      )}
                    </Box>
                  ) : c.label}
                </TableCell>
              ))}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No users found. Click "Create User" to add a new user.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
              <TableRow key={user.username}>
                {visibleColumns.map((c) => (
                  <TableCell key={c.key} sx={c.sx}>{c.render(user)}</TableCell>
                ))}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDetails(user.username)}
                    title="View Details"
                  >
                    <InfoIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog('password', user)}
                    title="Reset Password"
                  >
                    <KeyIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog('edit', user)}
                    title="Edit"
                  >
                    <EditIcon />
                  </IconButton>
                  {user.enabled ? (
                    <IconButton
                      size="small"
                      onClick={() => handleDisable(user.username)}
                      title="Disable User"
                      color="warning"
                    >
                      <BlockIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => handleEnable(user.username)}
                      title="Enable User"
                      color="success"
                    >
                      <EnableIcon />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(user.username)}
                    title="Delete"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationControls
        count={totalCount}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' && 'Create User'}
          {dialogMode === 'edit' && 'Edit User'}
          {dialogMode === 'password' && 'Reset Password'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogMode === 'create' && (
              <>
                <TextField
                  fullWidth
                  label="Username *"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  margin="normal"
                  required
                  helperText="Unique username for the user"
                />
                <TextField
                  fullWidth
                  label="Password *"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  margin="normal"
                  required
                  helperText="Minimum 8 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="First Name *"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    margin="normal"
                    required
                  />
                  <TextField
                    label="Middle Initial"
                    value={formData.middle_initial}
                    onChange={(e) => setFormData({ ...formData, middle_initial: e.target.value })}
                    margin="normal"
                    sx={{ width: '120px' }}
                    inputProps={{ maxLength: 10 }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Surname *"
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  margin="normal"
                  required
                />
                <Autocomplete
                  options={managers}
                  loading={loadingManagers}
                  getOptionLabel={(option) => `${option.username} - ${option.display_name || option.username}`}
                  value={managers.find(m => m.dn === formData.manager) || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, manager: newValue ? newValue.dn : '' });
                  }}
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase();
                    return options.filter(option =>
                      option.username.toLowerCase().includes(inputValue) ||
                      (option.display_name && option.display_name.toLowerCase().includes(inputValue))
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Manager *"
                      margin="normal"
                      required
                      helperText="Select the user's manager (type to search)"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{option.username}</div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>{option.display_name}</div>
                      </div>
                    </li>
                  )}
                  sx={{ mt: 1 }}
                  isOptionEqualToValue={(option, value) => option.dn === value.dn}
                />
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  margin="normal"
                  helperText="Job title (optional)"
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    margin="normal"
                    helperText="Employee type/role (optional)"
                  />
                  <TextField
                    fullWidth
                    label="Department"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    margin="normal"
                    helperText="Department name (optional)"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Mobile Phone"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    margin="normal"
                    helperText="Mobile phone number (optional)"
                  />
                  <TextField
                    fullWidth
                    label="Office Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    margin="normal"
                    helperText="Office phone number (optional)"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Logon Workstations"
                  value={formData.logon_workstations}
                  onChange={(e) => setFormData({ ...formData, logon_workstations: e.target.value })}
                  margin="normal"
                  helperText="Comma-separated list of computer names the user can log onto (optional)"
                />
                <Autocomplete
                  options={ous}
                  getOptionLabel={(option) => `${option.name || option} (${option.dn || ''})`}
                  value={ous.find(ou => ou.name === formData.ou) || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, ou: newValue ? newValue.name : '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organizational Unit"
                      margin="normal"
                      helperText="Select an OU or leave blank for default (optional)"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{option.name}</div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>{option.dn}</div>
                      </div>
                    </li>
                  )}
                  freeSolo={false}
                  sx={{ mt: 1 }}
                />
              </>
            )}
            {dialogMode === 'edit' && (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    margin="normal"
                  />
                  <TextField
                    label="Middle Initial"
                    value={formData.middle_initial}
                    onChange={(e) => setFormData({ ...formData, middle_initial: e.target.value })}
                    margin="normal"
                    sx={{ width: '120px' }}
                    inputProps={{ maxLength: 10 }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Surname"
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  margin="normal"
                />
                <Autocomplete
                  options={managers}
                  loading={loadingManagers}
                  getOptionLabel={(option) => `${option.username} - ${option.display_name || option.username}`}
                  value={managers.find(m => m.dn === formData.manager) || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, manager: newValue ? newValue.dn : '' });
                  }}
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase();
                    return options.filter(option =>
                      option.username.toLowerCase().includes(inputValue) ||
                      (option.display_name && option.display_name.toLowerCase().includes(inputValue))
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Manager"
                      margin="normal"
                      helperText="Select the user's manager (type to search)"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{option.username}</div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>{option.display_name}</div>
                      </div>
                    </li>
                  )}
                  sx={{ mt: 1 }}
                  isOptionEqualToValue={(option, value) => option.dn === value.dn}
                />
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  margin="normal"
                  helperText="Job title"
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    margin="normal"
                    helperText="Employee type/role"
                  />
                  <TextField
                    fullWidth
                    label="Department"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    margin="normal"
                    helperText="Department name"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Mobile Phone"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Office Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    margin="normal"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Logon Workstations"
                  value={formData.logon_workstations}
                  onChange={(e) => setFormData({ ...formData, logon_workstations: e.target.value })}
                  margin="normal"
                  helperText="Comma-separated list of computer names"
                />
                <TextField
                  fullWidth
                  label="Company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  margin="normal"
                  multiline
                  rows={2}
                />
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.street_address}
                  onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                  margin="normal"
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="City"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="State/Province"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    margin="normal"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Postal Code"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    margin="normal"
                  />
                </Box>
              </>
            )}
            {dialogMode === 'password' && (
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                margin="normal"
                required
                helperText="Minimum 8 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddEntitlementsDialog
        open={entitlementsDialogOpen}
        onClose={() => setEntitlementsDialogOpen(false)}
        onSuccess={() => dispatch(fetchUsers(true))}
      />

      <UserDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        username={selectedUsername}
      />
    </Box>
  );
}

export default Users;
